import axios from "axios";
import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import { baseUrl, userName, ipAddress } from "./Univ.js";

function AddVisit() {
  const [booths, setBooths] = useState([]);
  const [streets, setStreets] = useState([]);
  const [houses, setHouses] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    booth: "",
    street: "",
    house: "",
    volunteer: "",
    feedback: "",
    mood: "3",
    follow_up_required: false,
  });

  /* ---------------- FETCH MASTER DATA ---------------- */
  useEffect(() => {
    axios.get(baseUrl + "booths/").then(r => setBooths(r.data));
    axios.get(baseUrl + "streets/").then(r => setStreets(r.data));
    axios.get(baseUrl + "houses/").then(r => setHouses(r.data));
    axios.get(baseUrl + "volunteers/").then(r => setVolunteers(r.data));
    fetchVisits();
  }, []);

  /* ---------------- FETCH VISITS ---------------- */
  const fetchVisits = () => {
    setIsLoading(true);
    axios.get(baseUrl + "visits/")
      .then(res => {
        setRows(res.data);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  /* ---------------- HANDLE CHANGE ---------------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  /* ---------------- SUBMIT ---------------- */
  const formSubmit = () => {
    if (!form.booth || !form.street || !form.house) {
      Swal.fire("Error", "Booth, Street & House are required", "error");
      return;
    }

    const _formData = new FormData();
    _formData.append("booth", form.booth);
    _formData.append("street", form.street);
    _formData.append("house", form.house);
    _formData.append("volunteer", form.volunteer);
    _formData.append("feedback", form.feedback);
    _formData.append("mood", form.mood);
    _formData.append("follow_up_required", form.follow_up_required);
    _formData.append("addedby", userName);
    _formData.append("ip_add", ipAddress);

    axios.post(baseUrl + "visits/", _formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(res => {
      if (res.status === 200 || res.status === 201) {
        Swal.fire({
          title: "Visit added",
          icon: "success",
          timer: 2500,
          showConfirmButton: false,
        });
        setTimeout(() => window.location.reload(), 1000);
        setForm({
          booth: "",
          street: "",
          house: "",
          volunteer: "",
          feedback: "",
          mood: "3",
          follow_up_required: false,
        });
      }
    })
    .catch(err => {
      console.error(err.response?.data || err);
      Swal.fire("Error", "Failed to add visit", "error");
    });
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm",
      text: "Delete this visit?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(result => {
      if (result.isConfirmed) {
        axios.delete(baseUrl + "delete-visits/" + id + "/")
          .then(() => {
            Swal.fire("Deleted", "Visit removed", "success");
            window.location.reload();
          });
      }
    });
  };

  /* ---------------- DATAGRID COLUMNS ---------------- */
  const columns = [
    { field: "id", headerName: "ID", width: 80 },

    {
      field: "booth",
      headerName: "Booth",
      width: 130,
      renderCell: (p) => p?.row?.booth?.booth_number || "—",
    },
    {
      field: "street",
      headerName: "Street",
      width: 200,
      renderCell: (p) => p?.row?.street?.name || "—",
    },
    {
      field: "house",
      headerName: "House",
      width: 120,
      renderCell: (p) => p?.row?.house?.house_no || "—",
    },
    {
      field: "volunteer",
      headerName: "Volunteer",
      width: 180,
      renderCell: (p) => p?.row?.volunteer?.name || "—",
    },
    {
      field: "mood",
      headerName: "Mood",
      width: 120,
      renderCell: (p) => {
        if (p.value == 1) return "Very Bad";
        if (p.value == 3) return "Neutral";
        if (p.value == 5) return "Very Good";
        return "-";
      }
    },
    {
      field: "follow_up_required",
      headerName: "Follow Up",
      width: 130,
      renderCell: (p) => (p.value ? "Yes" : "No"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      renderCell: (params) => (
        <button
          className="btn btn-danger btn-sm"
          onClick={() => handleDelete(params.row.id)}
        >
          Delete
        </button>
      ),
    },
  ];

  /* ---------------- UI ---------------- */
  return (
    <section className="container mt-4">
      <div className="card">
        <h4 className="card-header">Add Door-to-Door Visit</h4>
        <div className="card-body">

          <select className="form-control mb-3" name="booth" value={form.booth} onChange={handleChange}>
            <option value="">-- Select Booth --</option>
            {booths.map(b => (
              <option key={b.id} value={b.id}>Booth {b.booth_number}</option>
            ))}
          </select>

          <select className="form-control mb-3" name="street" value={form.street} onChange={handleChange}>
            <option value="">-- Select Street --</option>
            {streets.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <select className="form-control mb-3" name="house" value={form.house} onChange={handleChange}>
            <option value="">-- Select House --</option>
            {houses.map(h => (
              <option key={h.id} value={h.id}>{h.house_no}</option>
            ))}
          </select>

          <select className="form-control mb-3" name="volunteer" value={form.volunteer} onChange={handleChange}>
            <option value="">-- Select Volunteer --</option>
            {volunteers.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>

          <textarea
            className="form-control mb-3"
            name="feedback"
            placeholder="Feedback"
            value={form.feedback}
            onChange={handleChange}
          />

          <select className="form-control mb-3" name="mood" value={form.mood} onChange={handleChange}>
            <option value="1">Very Bad</option>
            <option value="3">Neutral</option>
            <option value="5">Very Good</option>
          </select>

          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              name="follow_up_required"
              checked={form.follow_up_required}
              onChange={handleChange}
            />
            <label className="form-check-label">Follow-up required</label>
          </div>

          <button className="btn btn-primary" onClick={formSubmit}>
            Save Visit
          </button>
        </div>
      </div>

      <div className="mt-4">
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          sx={{
            boxShadow: 4,
            border: 4,
            borderColor: "primary.light",
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
          }}
        />
      </div>
    </section>
  );
}

export default AddVisit;
